import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, User, LogOut, Camera, ListChecks } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { BrandMarkLink } from "./BrandMarkLink";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/staff/scanner", label: "Scanner", Icon: Camera, testId: "staff-topnav-scanner" },
  { to: "/staff/log", label: "Activity", Icon: ListChecks, testId: "staff-topnav-activity" },
];

const initialsOf = (user) => {
  const f = (user?.first_name || "").trim();
  const l = (user?.last_name || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "S";
  return (user?.email || "S").charAt(0).toUpperCase();
};

const TopLink = ({ to, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      [
        "relative font-body font-semibold text-[12px] tracking-[0.18em] uppercase",
        "px-1 py-2 transition-colors duration-200",
        isActive ? "text-napoli-gold" : "text-napoli-cream/75 hover:text-napoli-cream",
      ].join(" ")
    }
  >
    {({ isActive }) => (
      <>
        {label}
        {isActive ? (
          <span aria-hidden className="absolute -bottom-[3px] left-0 right-0 h-[2px] bg-napoli-gold" />
        ) : null}
      </>
    )}
  </NavLink>
);

export const StaffTopNav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = initialsOf(user);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Staff";

  return (
    <header
      data-testid="staff-top-nav"
      className="hidden md:block sticky top-0 z-30 bg-napoli-espresso border-b border-napoli-gold/20"
    >
      <div className="max-w-7xl mx-auto px-8 h-[72px] flex items-center gap-8">
        <BrandMarkLink size="sm" theme="dark" testId="staff-topnav-brand" ariaLabel="Napoli — staff portal" />

        <nav className="flex-1 flex items-center justify-center gap-10" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <TopLink key={l.to} {...l} />
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <span
            data-testid="staff-topnav-role-badge"
            className="hidden lg:inline-flex items-center gap-2 px-3 py-1 border border-napoli-gold/40 text-napoli-gold font-body text-[10px] tracking-[0.22em] uppercase"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-napoli-gold" aria-hidden />
            Staff
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="staff-topnav-account-trigger"
                className="flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-napoli-gold/60 rounded-full"
              >
                <span className="hidden xl:inline font-body text-[12px] text-napoli-cream/80">
                  {fullName}
                </span>
                <span
                  aria-hidden
                  className="h-9 w-9 rounded-full bg-napoli-oxblood text-napoli-cream flex items-center justify-center font-display font-bold text-sm group-hover:bg-napoli-oxblood-dark transition-colors"
                >
                  {initials}
                </span>
                <ChevronDown size={14} className="text-napoli-cream/60 group-hover:text-napoli-cream transition-colors" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              className="min-w-[220px] bg-napoli-marble border-napoli-gold/30 rounded-none p-1"
              data-testid="staff-topnav-account-menu"
            >
              <DropdownMenuLabel className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft px-3 py-2">
                {fullName} · Staff
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-napoli-gold/20" />
              <DropdownMenuItem
                data-testid="staff-topnav-menu-profile"
                onSelect={() => navigate("/staff/profile")}
                className="font-body text-[13px] text-napoli-espresso focus:bg-napoli-cream-warm focus:text-napoli-espresso rounded-none cursor-pointer px-3 py-2.5"
              >
                <User size={16} className="text-napoli-oxblood" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-napoli-gold/20" />
              <DropdownMenuItem
                data-testid="staff-topnav-menu-logout"
                onSelect={() => logout("/")}
                className="font-body text-[13px] text-napoli-oxblood focus:bg-napoli-cream-warm focus:text-napoli-oxblood-dark rounded-none cursor-pointer px-3 py-2.5"
              >
                <LogOut size={16} />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default StaffTopNav;
